import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { DEFAULT_SETTINGS, useSettings } from '@/features/settings';
import { TracksOverlayMap } from '@/features/map/TracksOverlayMap';
import { useTrackRoutes } from '@/features/map/use-track-routes';
import { type DateFormat, formatCalendarDate, formatCount, formatLocalDate } from '@/lib/format';
import { getPalette, sampleColour } from '@/lib/palette';

import {
  type LogbookFilter,
  matchesLogbook,
  matchesSummaryQuery,
  matchesSummarySource,
  type SourceFilter,
} from './filters';
import { type FlightSummary, routeLabel } from './summary';
import { useFlightSummaries } from './use-flight-summaries';

/**
 * Every flight the pilot has, drawn on one map at the top of the flights page.
 *
 * The list below it answers "what did I fly"; this answers "where do I fly",
 * which no list can — a hundred rows reading NZAR–NZAR are a hundred rows, and
 * the same hundred flights on a map are the shape of a training area. The two
 * are the same set at all times: search or filter the list and the map narrows
 * with it, which is what makes it a view of the search rather than decoration
 * above it.
 *
 * ## Why this reads the database again
 *
 * `FlightList` runs the same joined live query. Lifting it into the route and
 * handing both components the result was the obvious alternative and was not
 * worth what it cost: `FlightList` fetching its own rows is what its tests
 * render against, and every other page that lists flights does the same. The
 * duplicate is one extra read of a few hundred rows out of IndexedDB, on mount
 * and on a write — both queries re-fire together because Dexie fires every
 * subscription that touched a changed table anyway. The *expensive* half, the
 * point arrays, is not duplicated: only this component reads those, one track
 * at a time, and `useTrackRoutes` keeps the reduced result.
 *
 * ## Colour is this component's business, not the map's
 *
 * `TracksOverlayMap` draws whatever colour a route arrives with and knows
 * nothing about dates. This is where each drawn flight's `colour` is set: its
 * position between the oldest and newest flight *currently drawn* — narrowing
 * with a search narrows the range the same way it narrows the pins — sampled
 * along the pilot's chosen palette (`settings.defaultPaletteId`, the same one
 * `FlightMap` colours a single track's altitude or speed with). A flight with
 * no `startTime` sorts by its calendar date instead, same as the list below it.
 */

/** The same window `FlightList` reads through, so the map and the list can never disagree. */
const FETCH_LIMIT = 500;

/** Milliseconds to sort and colour by: the track's first fix, or midnight of the logged date. */
function whenMs(summary: FlightSummary): number {
  return Date.parse(summary.startTime ?? summary.date);
}

/** Same rule the hover card and the legend show a flight's date by. */
function dateLabel(
  summary: FlightSummary,
  options: { format: DateFormat; timeZone: string },
): string {
  return summary.startTime === undefined
    ? formatCalendarDate(summary.date, options)
    : formatLocalDate(summary.startTime, options);
}

export interface FlightsOverviewMapProps {
  query?: string;
  source?: SourceFilter;
  logbook?: LogbookFilter;
}

export function FlightsOverviewMap({
  query = '',
  source = 'all',
  logbook = 'all',
}: FlightsOverviewMapProps) {
  const fetched = useFlightSummaries(FETCH_LIMIT);
  const settings = useSettings() ?? DEFAULT_SETTINGS;
  const [activeTrackId, setActiveTrackId] = useState<string | null>(null);
  const navigate = useNavigate();

  const dateOptions = { format: settings.dateFormat, timeZone: settings.timeZone };

  /*
   * Every track, filtered or not, is what the loader is asked for — narrowing
   * it to the search would throw the reduced geometry away the moment the
   * search box was cleared. What the search narrows is what is *drawn*.
   */
  const allTrackIds = useMemo(
    () =>
      fetched === null
        ? null
        : fetched.flatMap((summary) => (summary.track === undefined ? [] : [summary.track.id])),
    [fetched],
  );
  const { byId, pending } = useTrackRoutes(allTrackIds);

  const matching = useMemo(
    () =>
      (fetched ?? []).filter(
        (summary) =>
          matchesSummaryQuery(summary, query) &&
          matchesSummarySource(summary, source) &&
          matchesLogbook(summary, logbook),
      ),
    [fetched, query, source, logbook],
  );

  // The summary alongside its route: `matching` already carries flights with
  // no track, and those have nothing to draw or to colour by.
  const drawn = useMemo(
    () =>
      matching.flatMap((summary) => {
        const route = summary.track === undefined ? undefined : byId.get(summary.track.id);
        return route === undefined ? [] : [{ summary, route }];
      }),
    [matching, byId],
  );

  // Newest first, inherited from `matching` — so `drawn[0]` is the newest
  // flight drawn and the last entry is the oldest, with no separate sort.
  // `null` when there is nothing to spread across a gradient: one flight
  // drawn, or every flight drawn landing at the same instant. `TrackRoute.colour`
  // then stays unset on every route, and the map falls back to its flat
  // accent colour.
  const range = useMemo(() => {
    const newest = drawn[0]?.summary;
    const oldest = drawn[drawn.length - 1]?.summary;
    if (oldest === undefined || newest === undefined) {
      return null;
    }
    const oldestMs = whenMs(oldest);
    const newestMs = whenMs(newest);
    return oldestMs < newestMs ? { oldest, newest, oldestMs, newestMs } : null;
  }, [drawn]);

  const palette = useMemo(() => getPalette(settings.defaultPaletteId), [settings.defaultPaletteId]);

  const routes = useMemo(
    () =>
      drawn.map(({ summary, route }) => ({
        ...route,
        ...(range === null
          ? {}
          : {
              colour: sampleColour(
                palette,
                (whenMs(summary) - range.oldestMs) / (range.newestMs - range.oldestMs),
              ),
            }),
      })),
    [drawn, range, palette],
  );

  const active = useMemo(
    () => matching.find((summary) => summary.track?.id === activeTrackId),
    [matching, activeTrackId],
  );

  if (fetched === null || fetched.length === 0) {
    return null;
  }

  const narrowed = matching.length !== fetched.length;

  return (
    <section aria-label="Flight map" className="space-y-1.5">
      <div className="relative isolate">
        <TracksOverlayMap
          routes={routes}
          activeTrackId={activeTrackId}
          onActiveTrackId={setActiveTrackId}
          onSelect={(trackId) => {
            void navigate({ to: '/flights/$trackId', params: { trackId } });
          }}
        />

        {active === undefined ? null : (
          <div
            className="border-border bg-surface/95 rounded-control pointer-events-none absolute top-3 left-3 z-(--z-overlay) max-w-[70%] border px-2.5 py-1.5 shadow-sm backdrop-blur"
            data-testid="overview-hover-card"
          >
            <p className="text-sm font-semibold tracking-tight">
              {active.registration ?? active.track?.callsign ?? 'Unknown aircraft'}
            </p>
            <p className="text-muted-foreground text-xs">
              {[routeLabel(active), dateLabel(active, dateOptions)]
                .filter((part) => part !== undefined && part !== '')
                .join(' · ')}
            </p>
          </div>
        )}

        {range === null ? null : (
          <div
            className="border-border bg-surface/95 rounded-control pointer-events-none absolute bottom-3 left-3 z-(--z-overlay) min-w-48 border p-2 text-xs whitespace-nowrap shadow-sm backdrop-blur"
            data-testid="overview-legend"
          >
            <div
              className="h-2 w-full rounded-full"
              style={{ background: `linear-gradient(to right, ${palette.stops.join(', ')})` }}
            />
            <div className="tabular text-muted-foreground mt-1 flex justify-between gap-4">
              <span>{dateLabel(range.oldest, dateOptions)}</span>
              <span>{dateLabel(range.newest, dateOptions)}</span>
            </div>
          </div>
        )}

        {routes.length === 0 && pending === 0 ? (
          <p
            className="border-border bg-surface/95 rounded-control text-muted-foreground absolute top-3 left-3 z-(--z-overlay) border px-2.5 py-1.5 text-xs shadow-sm backdrop-blur"
            data-testid="overview-empty"
          >
            {narrowed ? 'No flights match this search.' : 'No flight has a track to draw.'}
          </p>
        ) : null}
      </div>

      <p className="text-muted-foreground text-xs" data-testid="overview-count">
        {narrowed
          ? `${formatCount(routes.length)} of ${formatCount(fetched.length)} flights drawn.`
          : `${formatCount(routes.length)} flights drawn.`}
        {pending === 0 ? null : ` Reading ${formatCount(pending)} more…`}
      </p>
    </section>
  );
}
